import { Component, ViewChild, HostBinding } from '@angular/core';
import {
  Platform,
  NavController,
  MenuController,
  Events,
  LoadingController,
  ModalController,
  Config
} from 'ionic-angular';
import { Storage } from '@ionic/storage';
import { StatusBar } from '@ionic-native/status-bar';
import { SplashScreen } from '@ionic-native/splash-screen';
import { ScreenOrientation } from '@ionic-native/screen-orientation';
import { OneSignal } from '@ionic-native/onesignal';

import { TranslateService, LangChangeEvent } from '@ngx-translate/core';

import { MenuItem } from '../models/menu-item.model';
import { User } from '../models/user.model';
import { Language } from '../models/language.model';
import { Notification } from '../models/notification.model';

import { BrandSelectorPage } from '../pages/brand-selector/brand-selector';
import { LoginPage } from '../pages/auth/login';
import { WelcomePage } from '../pages/welcome/welcome';
import { BrandPage } from '../pages/brand/brand';
import { ProductCategoriesPage } from '../pages/product-categories/product-categories';
import { ChallengesPage } from '../pages/challenges/challenges';
import { ChallengeDetailPage } from '../pages/challenge-detail/challenge-detail';
import { PostsPage } from '../pages/posts/posts';
import { PostDetailPage } from '../pages/post-detail/post-detail';

import { PosmCategoriesPage } from '../pages/posm-categories/posm-categories';

import { QuizzesPage } from '../pages/quizzes/quizzes';
import { QuizDetailPage } from '../pages/quiz-detail/quiz-detail';
import { IncidentReportingPage } from '../pages/incident-reporting/incident-reporting';
import { ProfilePage } from '../pages/profile/profile';
import { RegisterUserPage } from '../pages/register-user/register-user';

import { TermsOfUsePage } from '../pages/terms-of-use/terms-of-use';
import { PrivacyPolicyPage } from '../pages/privacy-policy/privacy-policy';
import { NotificationsPage } from '../pages/notifications/notifications';

import { AuthProvider } from '../providers/auth/auth';
import { LanguageProvider } from '../providers/language/language';
import { NotificationProvider } from '../providers/notification/notification';
import { OrderProvider } from '../providers/order/order';

import { fadeInOutAnimation } from '../animations/fade-in-out';

@Component({
  templateUrl: 'app.html',
  animations: [fadeInOutAnimation]
})
export class AskMarcApp {
  @ViewChild('content') nav: NavController;

  @HostBinding('class.desktop-app') public isDesktop: boolean = false;

  rootPage: any;

  activePage: string;

  menuItems: MenuItem[];

  user: User;

  brand_logo_url = '';
  brand_white_logo_url = '';
  brand_name = '';

  languageSwitcherOpened: boolean = false;
  currentLanguage: string;
  langs: Array<Language>;

  notifications: Notification[];

  constructor(
    private storage: Storage,
    private screenOrientation: ScreenOrientation,
    platform: Platform,
    statusBar: StatusBar,
    splashScreen: SplashScreen,
    public menuCtrl: MenuController,
    public events: Events,
    public loadingCtrl: LoadingController,
    public modalCtrl: ModalController,
    public config: Config,
    public translate: TranslateService,
    private oneSignal: OneSignal,
    public languageProvider: LanguageProvider,
    public notificationProvider: NotificationProvider,
    private authProvider: AuthProvider,
    private orderProvider: OrderProvider
  ) {
    platform.ready().then(() => {
      statusBar.styleBlackOpaque();

      // if we are on cordova platform
      if (platform.is('cordova')) {
        // lock screen orientation
        this.screenOrientation.lock(
          this.screenOrientation.ORIENTATIONS.PORTRAIT
        );

        // setup notifications
        this.setupNotifications();
      }

      // if is desktop
      if (platform.is('core')) {
        splashScreen.hide();
        this.isDesktop = true;
      }

      // set available languages
      this.languageProvider.getLangs().subscribe((langs: Language[]) => {
        this.translate.addLangs(langs.map(l => l.locale));
        this.langs = langs;
      });

      // get language stored
      let locale: string =
        localStorage.getItem('locale') ||
        this.translate.getBrowserLang() ||
        'en';

      // default language
      this.translate.setDefaultLang(locale);

      // set language
      this.translate.use(locale);
      this.currentLanguage = locale;

      // change back button string
      this.translate.get('BTN_BACK').subscribe((res: string) => {
        this.config.set('backButtonText', res);
      });

      // if lang changed, retranslate the back button
      this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.translate.get('BTN_BACK').subscribe((res: string) => {
          this.config.set('backButtonText', res);
        });
      });

      // set active page
      this.getProfile()
        .then(() => {
          if (this.authProvider.isLoggedIn()) {
            if (this.user && this.user.role_name == 'mwc') {
              this.activePage = 'product_categories';
            }
          } else {
            this.activePage = 'posts';
          }

          if (!this.nav.getActive()) {
            if (this.authProvider.isLoggedIn()) {
              this.storage.get('welcome').then(welcome => {
                if (this.user && this.user.role_name == 'mwc') {
                  this.rootPage = ProductCategoriesPage;
                } else {
                  this.rootPage = welcome ? BrandSelectorPage : WelcomePage;
                }
                splashScreen.hide();
              });
            } else {
              this.rootPage = LoginPage;
              splashScreen.hide();
            }
          } else {
            splashScreen.hide();
          }
        })
        .catch(error => {
          if (
            !this.nav.getActive() ||
            this.nav.getActive().name !== 'PublicPostDetailPage'
          ) {
            console.log(error);
            this.rootPage = LoginPage;
          }

          splashScreen.hide();
        });

      this.notificationProvider.notifications.subscribe(
        (notifications: Notification[]) => (this.notifications = notifications)
      );
    });

    events.subscribe('auth:logged', () => {
      this.getProfile().then();
    });

    events.subscribe('profile:refresh', () => {
      this.getProfile().then();
    });

    events.subscribe('language:open', () => {
      this.languageSwitcherOpened = true;
    });

    events.subscribe('notifications:open', () => {
      this.openNotificationsModal();
    });

    events.subscribe(
      'brand:refresh',
      (brand_name, brand_logo_url, brand_white_logo_url) => {
        this.brand_name = brand_name;
        this.brand_logo_url = brand_logo_url;
        this.brand_white_logo_url = brand_white_logo_url;
        const current_brand = {
          name: this.brand_name,
          logo_url: this.brand_logo_url,
          brand_white_logo_url: this.brand_white_logo_url
        };
        this.storage.set('current_brand', current_brand);
        this.storage.set('basket', new Array());
      }
    );

    events.subscribe('orderList:get_data', () => {
      let newOrderList;
      this.orderProvider.getOrderList().subscribe(
        orderList => {
          newOrderList = orderList;
        },
        error => {
          newOrderList = new Array();
        },
        () => {
          events.publish('orderList:refresh', newOrderList);
        }
      );
    });
  }

  private setupNotifications() {
    this.oneSignal.startInit(
      '8f45bfa3-f48a-4172-90b9-604ec851c0fb',
      '36150380417'
    );

    this.oneSignal.inFocusDisplaying(
      this.oneSignal.OSInFocusDisplayOption.InAppAlert
    );

    // get ids of the registered user
    this.oneSignal
      .getIds()
      .then(ids => {
        this.updatePushID(ids.userId);
      })
      .catch(err => {
        console.log(err);
      });

    // do something when notification is received
    this.oneSignal.handleNotificationReceived().subscribe(notification => {
      //console.log('handleNotificationReceived', notification);
      /* if (notification.payload.additionalData) {
        if (notification.payload.additionalData.type === 'post') {
          this.nav.push(PostDetailPage, {
            id: notification.payload.additionalData.id
          });
        }
        if (notification.payload.additionalData.type === 'challenge') {
          this.nav.push(ChallengeDetailPage, {
            id: notification.payload.additionalData.id
          });
        }
        if (notification.payload.additionalData.type === 'quiz') {
          this.nav.push(QuizDetailPage, {
            id: notification.payload.additionalData.id
          });
        }
      } */
    });

    // do something when a notification is opened
    this.oneSignal.handleNotificationOpened().subscribe(notification => {
      //console.log('handleNotificationOpened', notification);
      if (notification.notification.payload.additionalData) {
        if (notification.notification.payload.additionalData.type === 'post') {
          this.nav.push(PostDetailPage, {
            id: notification.notification.payload.additionalData.id
          });
        }
        if (
          notification.notification.payload.additionalData.type === 'challenge'
        ) {
          this.nav.push(ChallengeDetailPage, {
            id: notification.notification.payload.additionalData.id
          });
        }
        if (notification.notification.payload.additionalData.type === 'quiz') {
          this.nav.push(QuizDetailPage, {
            id: notification.notification.payload.additionalData.id
          });
        }
      }
    });

    this.oneSignal.endInit();
  }

  private getProfile() {
    return new Promise((resolve, reject) => {
      this.authProvider.profile().subscribe(
        user => {
          this.user = new User(user);
          this.events.publish(
            'brand:refresh',
            this.user.brand_name.toLowerCase(),
            this.user.brand_logo_url,
            this.user.brand_white_logo_url
          );
          if (!this.user.tos_and_privacy && this.user.role_name != 'mwc') {
            this.tosAndPrivacyModals();
          }
          resolve();
        },
        error => {
          reject(error);
        }
      );
    });
  }

  private updatePushID(id: string) {
    let loading = this.loadingCtrl.create({
      content: this.translate.instant('MSG_PLEASE_WAIT')
    });

    loading.present();

    let values = {
      push_id: id
    };

    this.authProvider.updatePushID(values).subscribe(
      status => {},
      error => {
        loading.dismiss();
      },
      () => {
        loading.dismiss();
      }
    );
  }

  private tosAndPrivacyModals() {
    let privacyModal = this.modalCtrl.create(
      PrivacyPolicyPage,
      { showAcceptBtn: true },
      { cssClass: this.brand_name }
    );
    privacyModal.present();

    let tosModal = this.modalCtrl.create(
      TermsOfUsePage,
      { showAcceptBtn: true },
      { cssClass: this.brand_name }
    );
    tosModal.present();

    privacyModal.onDidDismiss(() => {
      this.acceptTosAndPrivacy();
    });
  }

  private acceptTosAndPrivacy() {
    let loading = this.loadingCtrl.create({
      content: this.translate.instant('MSG_PLEASE_WAIT')
    });

    loading.present();

    let values = {
      tos_and_privacy: true
    };

    this.authProvider.acceptTosAndPrivacy(values).subscribe(
      status => {},
      error => {
        loading.dismiss();
      },
      () => {
        loading.dismiss();
      }
    );
  }

  private openNotificationsModal() {
    let notificationsModal = this.modalCtrl.create(
      NotificationsPage,
      { showAcceptBtn: true },
      { cssClass: this.brand_name }
    );
    notificationsModal.present();
  }

  private updateLanguage(languageCode: string) {
    let loading = this.loadingCtrl.create({
      content: this.translate.instant('MSG_PLEASE_WAIT')
    });

    loading.present();

    let values = {
      language_code: languageCode
    };

    this.authProvider.updateLanguage(values).subscribe(
      status => {},
      error => {
        loading.dismiss();
      },
      () => {
        loading.dismiss();
      }
    );
  }

  switchLang(lang: Language) {
    // change used language
    this.translate.use(lang.locale);
    // assign the new language
    this.currentLanguage = lang.locale;
    // close the language switcher
    this.languageSwitcherOpened = false;
    // store the locale variable
    localStorage.setItem('locale', lang.locale);
    // update remote language
    this.updateLanguage(lang.code);
  }

  openPage(page) {
    this.menuCtrl.close();

    let p: any;

    switch (page) {
      case 'brand_selector':
        p = BrandSelectorPage;
        break;
      case 'brand':
        p = BrandPage;
        break;
      case 'product_categories':
        p = ProductCategoriesPage;
        break;
      case 'challenges':
        p = ChallengesPage;
        break;
      case 'posts':
        p = PostsPage;
        break;
      case 'posm_categories':
        p = PosmCategoriesPage;
        break;
      case 'quizzes':
        p = QuizzesPage;
        break;
      case 'incident_reporting':
        p = IncidentReportingPage;
        break;
      case 'register_user':
        p = RegisterUserPage;
        break;
      case 'profile':
        p = ProfilePage;
        break;
      default:
        break;
    }

    if (page != 'brand_selector') {
      this.activePage = page;
    } else {
      this.activePage = 'posts';
    }

    this.nav.setRoot(p);
  }

  checkActivePage(page): boolean {
    return page === this.activePage;
  }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts